import * as THREE from "three";

/**
 * The marks the placement preview draws over the map (placement.ts puts them together): the chosen road option's
 * glowing, marching edge, the numbered option badges, and the build-here plus on each legal tower cell. Kept apart
 * so the geometry rules are testable without a scene, and placement.ts stays about what is shown when.
 */

export interface P2 { readonly x: number; readonly y: number }

/**
 * The two edges of a road-wide band along `path` (cell centres, grid x/y), `halfWidth` either side, each running from
 * the start to a round cap at the far end where they meet. Corners are mitred, so an edge stays `halfWidth` from the
 * centre line on both legs of a turn. The start is left open: the band joins the road already laid there.
 */
export function roadEdges(path: readonly P2[], halfWidth: number, capSteps = RoadEdge.CapSteps): { left: P2[]; right: P2[] } {
  const left: P2[] = [], right: P2[] = [];
  if (path.length < 2) return { left, right };
  const dir = (a: P2, b: P2) => { const dx = b.x - a.x, dy = b.y - a.y, l = Math.hypot(dx, dy) || 1; return { x: dx / l, y: dy / l }; };
  const normal = (d: P2) => ({ x: -d.y, y: d.x });
  for (let i = 0; i < path.length; i++) {
    const nIn = i > 0 ? normal(dir(path[i - 1], path[i])) : null;
    const nOut = i < path.length - 1 ? normal(dir(path[i], path[i + 1])) : null;
    let m = nIn ?? nOut!, len = halfWidth;
    if (nIn && nOut) {
      const sx = nIn.x + nOut.x, sy = nIn.y + nOut.y, sl = Math.hypot(sx, sy);
      if (sl > 1e-6) { m = { x: sx / sl, y: sy / sl }; len = halfWidth / Math.max(RoadEdge.MinMiterCos, m.x * nIn.x + m.y * nIn.y); }
    }
    left.push({ x: path[i].x + m.x * len, y: path[i].y + m.y * len });
    right.push({ x: path[i].x - m.x * len, y: path[i].y - m.y * len });
  }
  // round cap: each edge swings a quarter turn from its side to the tip ahead of the last centre
  const end = path[path.length - 1], d = dir(path[path.length - 2], end), n = normal(d);
  for (let k = 1; k <= capSteps; k++) {
    const a = (k / capSteps) * (Math.PI / 2), c = Math.cos(a) * halfWidth, s = Math.sin(a) * halfWidth;
    left.push({ x: end.x + n.x * c + d.x * s, y: end.y + n.y * c + d.y * s });
    right.push({ x: end.x - n.x * c + d.x * s, y: end.y - n.y * c + d.y * s });
  }
  return { left, right };
}

export const RoadEdge = {
  /** Segments in each edge's quarter of the round cap. */
  CapSteps: 6,
  /** Floor on the mitre's cosine, so a hairpin cannot throw a corner point off across the map. */
  MinMiterCos: 0.35,
  /** Dashes per cell along the edge, and how fast they march towards the far end (cells per second). */
  DashesPerCell: 2.2,
  MarchCellsPerSecond: 1.6,
};

/**
 * A flat line of `width` along `points` at the height `h` gives it, as one mesh: a quad per step and a small fan at
 * each point for a round join. Every vertex carries `along`, its distance from the first point, for the dashes.
 */
export function lineMesh(points: readonly P2[], width: number, h: (x: number, z: number) => number, material: THREE.Material): THREE.Mesh {
  const pos: number[] = [], along: number[] = [];
  const r = width / 2;
  const v = (x: number, z: number, a: number) => { pos.push(x, h(x, z), z); along.push(a); };
  let dist = 0;
  const fan = RoadEdge.CapSteps * 2;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (i > 0) {
      const q = points[i - 1], dx = p.x - q.x, dz = p.y - q.y, len = Math.hypot(dx, dz) || 1;
      const nx = (-dz / len) * r, nz = (dx / len) * r, d0 = dist;
      dist += len;
      v(q.x + nx, q.y + nz, d0); v(p.x + nx, p.y + nz, dist); v(p.x - nx, p.y - nz, dist);
      v(q.x + nx, q.y + nz, d0); v(p.x - nx, p.y - nz, dist); v(q.x - nx, q.y - nz, d0);
    }
    for (let k = 0; k < fan; k++) {
      const a0 = (k / fan) * Math.PI * 2, a1 = ((k + 1) / fan) * Math.PI * 2;
      v(p.x, p.y, dist); v(p.x + Math.cos(a1) * r, p.y + Math.sin(a1) * r, dist); v(p.x + Math.cos(a0) * r, p.y + Math.sin(a0) * r, dist);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("along", new THREE.Float32BufferAttribute(along, 1));
  return new THREE.Mesh(g, material);
}

/**
 * The chosen option's edge: bright dashes marching from the open end to the far end, over everything (no depth test,
 * fog or tone mapping), so it reads as "this is where the road goes, and which way" on every chapter's ground. The
 * dashes alternate two warm tones rather than fade, so the line stays opaque and its round joins never double up.
 */
export function marchingEdgeMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: { value: 0 }, uA: { value: new THREE.Color(0xfff7d6) }, uB: { value: new THREE.Color(0xffa928) } },
    vertexShader: `attribute float along; varying float vAlong;
      void main() { vAlong = along; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `uniform float uTime; uniform vec3 uA; uniform vec3 uB; varying float vAlong;
      void main() {
        float k = step(0.5, fract(vAlong * ${RoadEdge.DashesPerCell.toFixed(2)} - uTime * ${(RoadEdge.MarchCellsPerSecond * RoadEdge.DashesPerCell).toFixed(2)}));
        gl_FragColor = vec4(mix(uA, uB, k), 1.0);
        #include <colorspace_fragment>
      }`,
    transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide,
  });
}

/**
 * A round numbered badge for a road option ("1", "2", …), gold for the one shown and white for the others, as a
 * texture made once per number and state. Null where there is no DOM (the unit tests).
 */
export function optionBadge(n: number, chosen: boolean): THREE.Texture | null {
  if (typeof document === "undefined") return null;
  const key = `${n}|${chosen}`;
  const hit = badgeCache.get(key);
  if (hit) return hit;
  const S = OptionBadge.TexturePx, cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const g = cv.getContext("2d");
  if (!g) return null;
  g.beginPath(); g.arc(S / 2, S / 2, S / 2 - 2, 0, Math.PI * 2);
  g.fillStyle = "#1c1206"; g.fill();
  g.beginPath(); g.arc(S / 2, S / 2, S / 2 - S * 0.09, 0, Math.PI * 2);
  g.fillStyle = chosen ? "#ffc629" : "#f4f1e6"; g.fill();
  g.fillStyle = "#1c1206"; g.font = `900 ${Math.round(S * 0.6)}px 'Fredoka Display', system-ui, sans-serif`; g.textAlign = "center"; g.textBaseline = "middle";
  g.fillText(String(n), S / 2, S / 2 + S * 0.04);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  badgeCache.set(key, t);
  return t;
}
const badgeCache = new Map<string, THREE.Texture>();

export const OptionBadge = {
  TexturePx: 64,
  /** World size of a badge (cells), and how high it floats over the ground at an option's far end. */
  Size: 0.52,
  Lift: 0.55,
  /** The shown option's badge floats higher, over its ghost portal arch. */
  ChosenLift: 1.7,
};

/**
 * A plus sign in the XY plane like `quad` (a build-here mark): arms `half` long and `thick` wide. Shape geometry, so
 * the caller rotates it flat and shares it.
 */
export function plusGeometry(half: number, thick: number): THREE.ShapeGeometry {
  const h = half, t = thick / 2;
  const pts = [[-t, -h], [t, -h], [t, -t], [h, -t], [h, t], [t, t], [t, h], [-t, h], [-t, t], [-h, t], [-h, -t], [-t, -t]];
  return new THREE.ShapeGeometry(new THREE.Shape(pts.map(([x, y]) => new THREE.Vector2(x, y))));
}
